import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

export function useTheme() {
  // 1. Just read what theme-init.js already did! It's safer and prevents React mismatches.
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }

    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });

  // 2. Update the DOM when the React state changes
  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
  }, [theme]);

  // 3. Listen for OS theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    function handleChange(e: MediaQueryListEvent) {
      // Only auto-switch if the user hasn't saved a manual preference
      if (!localStorage.getItem('theme')) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    }

    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  function toggleTheme() {
    setTheme((prev) => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      // ONLY save to localStorage when they actively click the toggle
      localStorage.setItem('theme', newTheme);

      return newTheme;
    });
  }

  return { theme, toggleTheme };
}
